import { Box } from '@mui/material';
import type { EquipmentTableData } from '@/equipments/application/types/EquipmentTypes';
import { CrewField, SelectedEquipment } from '../RaptorJobCreateModal';
import { OptionType, useERPEquipmentSelector } from '../hook/useERPEquipmentSelector';
import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import { useSnackbar } from '@/common/presentation/SnackbarProvider';
import { EquipmentFilterSection } from './EquipmentFilterSection';
import { EquipmentSelectionSection } from './EquipmentSelectionSection';

interface EquipmentResource {
  type?: {
    id?: number | string | null;
  } | null;
}

interface ERPEquipmentSelectorProps {
  selectedClass: OptionType | null;
  setSelectedClass: (value: OptionType | null) => void;
  selectedType: OptionType | null;
  setSelectedType: (value: OptionType | null) => void;
  selectedSize: OptionType | null;
  setSelectedSize: (value: OptionType | null) => void;
  selectedMaxSize: OptionType | null;
  setSelectedMaxSize: (value: OptionType | null) => void;
  strict: boolean;
  setStrict: (value: boolean) => void;
  equipmentSearchTerm: string;
  setEquipmentSearchTerm: (term: string) => void;
  selectedEquipment: SelectedEquipment | null;
  setSelectedEquipment: (equipment: SelectedEquipment | null) => void;
  crewFields: CrewField[];
  setCrewFields: (fields: CrewField[]) => void;
  startDate?: Date | null;
  customerId?: number;
  jobSiteId?: number;
  isEquipmentCompliant?: (equipment: EquipmentResource) => boolean;
  getEquipmentMissingCerts?: (equipment: EquipmentResource) => string[];
}

export const ERPEquipmentSelector = ({
  selectedClass,
  setSelectedClass,
  selectedType,
  setSelectedType,
  selectedSize,
  setSelectedSize,
  selectedMaxSize,
  setSelectedMaxSize,
  strict,
  setStrict,
  equipmentSearchTerm,
  setEquipmentSearchTerm,
  selectedEquipment,
  setSelectedEquipment,
  crewFields,
  setCrewFields,
  startDate,
  customerId,
  jobSiteId,
  isEquipmentCompliant,
  getEquipmentMissingCerts
}: ERPEquipmentSelectorProps) => {
  const { setFieldValue, errors, touched } = useFormikContext<ERPJobFormValues>();
  const { showSnackbar } = useSnackbar();
  const {
    filteredClasses,
    filteredTypes,
    filteredSizes,
    handleClassChange,
    handleTypeChange,
    handleSizeChange,
    handleEquipmentSelect,
    handleEquipmentDeselect,
    apiEquipments,
    loadingEquipments,
    loadingEquipmentFormOptions
  } = useERPEquipmentSelector({
    selectedClass,
    setSelectedClass,
    selectedType,
    setSelectedType,
    selectedSize,
    setSelectedSize,
    equipmentSearchTerm,
    setSelectedEquipment,
    startDate,
    crewFields,
    setCrewFields,
    customerId,
    jobSiteId
  });

  const defaultEmployeeByEquipmentRef = useRef<Record<number, number>>({});
  const previousEquipmentIdRef = useRef<number | null>(selectedEquipment?.id ?? null);

  useEffect(() => {
    const previousEquipmentId = previousEquipmentIdRef.current;
    const currentEquipmentId = selectedEquipment?.id ?? null;

    if (previousEquipmentId && previousEquipmentId !== currentEquipmentId) {
      let defaultEmployeeId = defaultEmployeeByEquipmentRef.current[previousEquipmentId];
      if (!defaultEmployeeId) {
        const equipment = apiEquipments?.find((item) => item.id === previousEquipmentId);
        defaultEmployeeId = equipment?.defaultEmployee?.id ?? 0;
        if (defaultEmployeeId) {
          defaultEmployeeByEquipmentRef.current[previousEquipmentId] = defaultEmployeeId;
        }
      }

      if (defaultEmployeeId) {
        const nextCrewFields = crewFields.filter((crew) => crew.employeeId !== defaultEmployeeId);
        if (nextCrewFields.length !== crewFields.length) {
          setCrewFields(nextCrewFields);
          currentEquipmentId === null && showSnackbar('Success', 'Unit and assigned operator successfully removed.', 'success');
        }
      }
    }

    if (currentEquipmentId) {
      const equipment = apiEquipments?.find((item) => item.id === currentEquipmentId);
      const defaultEmployeeId = equipment?.defaultEmployee?.id ?? 0;
      if (defaultEmployeeId) {
        defaultEmployeeByEquipmentRef.current[currentEquipmentId] = defaultEmployeeId;
      }
    }

    previousEquipmentIdRef.current = currentEquipmentId;
  }, [apiEquipments, crewFields, selectedEquipment?.id, setCrewFields, showSnackbar]);

  const handleStrictChange = (checked: boolean) => {
    setStrict(checked);
    setFieldValue('strictSize', checked);
  };

  const handleMaxSizeChange = (value: OptionType | null) => {
    setSelectedMaxSize(value);
    setFieldValue('equipmentMaxSizeId', value?.id ? Number(value.id) : null);
  };

  const handleToggleEquipment = (equipment: EquipmentTableData) => {
    if (selectedEquipment?.id === equipment.id) {
      handleEquipmentDeselect();
      return;
    }
    handleEquipmentSelect({
      id: equipment.id,
      name: equipment.name,
      class: equipment.class,
      type: equipment.type,
      size: equipment.size,
      status: equipment.status,
      defaultEmployee: equipment.defaultEmployee
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <EquipmentFilterSection
        filteredClasses={filteredClasses}
        filteredTypes={filteredTypes}
        filteredSizes={filteredSizes}
        selectedClass={selectedClass}
        selectedType={selectedType}
        selectedSize={selectedSize}
        selectedMaxSize={selectedMaxSize}
        strict={strict}
        loadingEquipmentFormOptions={loadingEquipmentFormOptions}
        loadingEquipments={loadingEquipments}
        equipmentSizeTouched={Boolean(touched.equipmentSizeId)}
        equipmentSizeError={touched.equipmentSizeId ? errors.equipmentSizeId : undefined}
        onClassChange={handleClassChange}
        onTypeChange={handleTypeChange}
        onSizeChange={handleSizeChange}
        onMaxSizeChange={handleMaxSizeChange}
        onStrictChange={handleStrictChange}
      />

      <EquipmentSelectionSection
        selectedEquipment={selectedEquipment}
        equipmentSearchTerm={equipmentSearchTerm}
        setEquipmentSearchTerm={setEquipmentSearchTerm}
        apiEquipments={apiEquipments}
        loadingEquipments={loadingEquipments}
        loadingEquipmentFormOptions={loadingEquipmentFormOptions}
        isEquipmentCompliant={isEquipmentCompliant}
        getEquipmentMissingCerts={getEquipmentMissingCerts}
        onToggleEquipment={handleToggleEquipment}
      />
    </Box>
  );
};
